import { Component, Input, ChangeDetectionStrategy } from "@angular/core";
import { CurrencyPipe } from "@angular/common";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { BudgetLabels } from "../budget.labels";

interface FiscalYearSummary {
    fiscalYear: string;
    totalBudgetsCreated: number;
    totalAllocated: number;
    pendingRequests: number;
}

@TypeManagerDecorator("s25-ng-budget-admin-summary")
@Component({
    selector: "s25-ng-budget-admin-summary",
    template: `
        @if (summary) {
            <!-- Summary Cards -->
            <div class="summary-section">
                <div class="summary-cards">
                    <div class="summary-card">
                        <div class="card-header">
                            <h3>{{ labels.ALLOCATIONS_CREATED }}</h3>
                        </div>
                        <div class="card-content">
                            <div class="amount">{{ summary.totalBudgetsCreated || 0 }}</div>
                            <div class="label">For {{ selectedFiscalYear }}</div>
                        </div>
                    </div>

                    <div class="summary-card">
                        <div class="card-header">
                            <h3>Total Allocated</h3>
                        </div>
                        <div class="card-content">
                            <div class="amount">
                                {{ summary.totalAllocated || 0 | currency: "USD" : "symbol" : "1.0-0" }}
                            </div>
                            <div class="label">{{ labels.ALLOCATION_ALLOCATION }}</div>
                        </div>
                    </div>

                    <div class="summary-card pending" [class.has-pending]="(summary.pendingRequests || 0) > 0">
                        <div class="card-header">
                            <h3>Pending Requests</h3>
                        </div>
                        <div class="card-content">
                            <div class="amount">{{ summary.pendingRequests || 0 }}</div>
                            <div class="label">Awaiting Review</div>
                        </div>
                    </div>
                </div>
            </div>
        }
    `,
    styles: `
        .summary-section {
            margin-bottom: 2rem;
        }

        .summary-cards {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
            gap: 1.5rem;
        }

        .summary-card {
            background: var(--color--surface-bg);
            border-radius: 8px;
            box-shadow: 0 2px 8px color-mix(in srgb, var(--color--text) 12%, transparent);
            overflow: hidden;
            transition:
                transform var(--transition-medium) ease,
                box-shadow var(--transition-medium) ease;
        }

        .summary-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 12px color-mix(in srgb, var(--color--text) 15%, transparent);
        }

        .summary-card .card-header {
            padding: 1rem;
            background: linear-gradient(
                135deg,
                var(--cn-blue),
                color-mix(in srgb, var(--cn-blue) 45%, var(--color--objects-location))
            );
            color: white;
        }

        .summary-card.pending .card-header {
            background: linear-gradient(
                135deg,
                var(--color--requested-changes),
                color-mix(in srgb, var(--color--star-yellow) 55%, var(--color--requested-changes))
            );
        }

        .summary-card.pending.has-pending .card-header {
            background: linear-gradient(
                135deg,
                var(--color--danger),
                color-mix(in srgb, var(--color--danger) 55%, var(--color--surface-bg))
            );
        }

        .card-header h3 {
            margin: 0;
            font-size: 1rem;
            font-weight: 600;
        }

        .card-content {
            padding: 1.5rem;
            text-align: center;
        }

        .amount {
            font-size: 2rem;
            font-weight: bold;
            color: var(--color--text);
            margin-bottom: 0.5rem;
        }

        .label {
            font-size: 0.9rem;
            color: var(--color--text-muted);
        }

        @media (max-width: 768px) {
            .summary-cards {
                grid-template-columns: 1fr;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [CurrencyPipe],
})
export class BudgetAdminSummaryComponent {
    readonly labels = BudgetLabels;

    @Input() summary: FiscalYearSummary | null = null;
    @Input() selectedFiscalYear: string = "";
}
